import { Line } from '@ant-design/charts'
import {
  Flex,
  Form,
  Select,
  Spin,
} from 'antd'
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { ReportsPageProps } from '@/components/feature/analytics/hooks/use-report-search'
import { FeatureKey } from '@/constants/feature-key'
import { NormalStatus } from '@/constants/normal-status'
import { readChatterOverviewComparison } from '@/redux/chatter-reports-actions'
import { selectEmployeeOptions } from '@/redux/employee-selector'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'
import { formatPrice } from '@/utils/formatter'

import ChartEmptyPlaceholder from '../common/chart-empty-placeholder'
import SectionTitle from '../section-title'

interface EmployeeFormValues {
  employeeIds: number[]
}

interface EmployeeSelectFormProps {
  onChange?: (value?: EmployeeFormValues) => void
}

export function EmployeeSelectForm({ onChange }: EmployeeSelectFormProps) {
  const [form] = Form.useForm<EmployeeFormValues>()
  const employeeOptions = useAppSelector(selectEmployeeOptions)

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const cascadeFilterOptions = useCallback((i: number) => {
    const options = employeeOptions.filter(option => {
      const filteredIds = selectedIds.filter((_, idx) => idx !== i)
      if (filteredIds.includes(option.value)) {
        return false
      }
      return option.status !== NormalStatus.Deactivated
    })
    return options
  }, [employeeOptions, selectedIds])

  return (
    <Form
      form={form}
      onValuesChange={(_, values) => {
        onChange?.(values)
      }}
    >
      <Flex gap="small">
        {new Array(3).fill(0).map((_, i) => {
          return (
            <Form.Item key={i} name={['employeeIds', i]}>
              <Select
                options={cascadeFilterOptions(i)}
                style={{ width: 100 }}
                onChange={(value: number) => {
                  selectedIds[i] = value
                  setSelectedIds(selectedIds.slice(0))
                }}
                notFoundContent={null}
                placeholder="Select"
                allowClear
              />
            </Form.Item>
          )
        })}
      </Flex>
    </Form>
  )
}

export default function ChatterOverviewComparison({ searchValues }: ReportsPageProps) {
  const { chatterOverviewComparison } = useAppSelector(state => state.chatterReports)
  const dispatch = useAppDispatch()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)

  useAsync(async () => {
    if (!selectedEmployeeIds.length) return
    setLoading(true)
    await dispatch(readChatterOverviewComparison({
      ...searchValues,
      employeeIds: selectedEmployeeIds,
      permissionKey: FeatureKey.ChatterReportsOverview,
    }))
    setLoading(false)
  }, [dispatch, searchValues, selectedEmployeeIds])

  const config = useMemo(() => {
    const data = chatterOverviewComparison
      ?.map(({
        employees,
        dateStr,
      }) => employees.map(earning => ({ dateStr, ...earning })))
      .flat() || []

    return {
      data,
      xField: 'dateStr',
      yField: 'earnings',
      seriesField: 'name',
      colorField: 'name',
      point: {
        zIndex: 2,
        style: {
          fill: 'white',
          lineWidth: 1.5,
        },
      },
      style: {
        lineWidth: 1.5,
      },
      tooltip: {
        title: 'dateStr',
        items: [
          (d: (typeof data)[number]) => {
            return {
              name: d.name,
              value: formatPrice()(d.earnings),
            }
          }],
      },
      legend: {
        color: {
          itemMarker: 'circle',
          title: false,
          position: 'bottom',
          rowPadding: 5,
          layout: {
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          },
        },
      },
      axis: {
        x: {
          labelAutoRotate: false,
        },
      },
      height: 280,
    }
  }, [chatterOverviewComparison])

  return (
    <Spin spinning={loading}>
      <Flex vertical flex={3} gap={8}>
        <SectionTitle
          title="Employee comparison"
          tooltip="Compare sales and performance of up to three employees"
        />
        <Flex>
          <EmployeeSelectForm
            onChange={v => setSelectedEmployeeIds(v?.employeeIds.filter(Boolean) || [])}
          />
        </Flex>
        {selectedEmployeeIds.length > 0 ?
          <Line {...config} /> :
          <ChartEmptyPlaceholder description="Select up to three employees to compare" />
        }
      </Flex>
    </Spin>
  )
}
